/**
 * PiStores.jsx — Redesigned · Dark Luxury Fintech
 * Accent: violet/indigo (Pi Stores identity)
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getAllStores, deleteStore, toggleStoreStatus, verifyStore, listenAllStores } from '../../services/storeService';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Store, CheckCircle, Eye, Trash2, RefreshCw, Search, Filter, ChevronDown, Phone, Mail, Star, Package, X, ShieldCheck, ToggleLeft, ToggleRight, Navigation, AlertTriangle } from 'lucide-react';
import FormatPrice from '../../components/FormatPrice';

// ─── Atoms ────────────────────────────────────────────────────────────────
const GlassPanel = ({ children, style = {} }) => (
  <div style={{ background: 'rgba(17,24,39,.85)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 16, backdropFilter: 'blur(16px)', ...style }}>{children}</div>
);

const MiniStat = ({ label, value, accent = '#a78bfa', icon: Icon, index = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * .07, duration: .45, ease: [.22,1,.36,1] }} whileHover={{ y: -3, transition: { duration: .15 } }}
    style={{ background: 'rgba(17,24,39,.85)', border: `1px solid ${accent}33`, borderRadius: 14, padding: '14px 16px', boxShadow: `0 0 24px ${accent}0d`, position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: -24, right: -24, width: 72, height: 72, borderRadius: '50%', background: `${accent}18`, filter: 'blur(20px)', pointerEvents: 'none' }} />
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
      <div>
        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: '#475569', marginBottom: 8 }}>{label}</p>
        <p style={{ fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{value}</p>
      </div>
      {Icon && <div style={{ width: 32, height: 32, borderRadius: 9, background: `${accent}18`, border: `1px solid ${accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon size={15} color={accent} strokeWidth={1.8} /></div>}
    </div>
    <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: .5, delay: .2 + index * .07 }} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: accent, opacity: .5, transformOrigin: 'left' }} />
  </motion.div>
);

const Btn = ({ children, onClick, variant = 'ghost', size = 'sm', disabled = false, style = {} }) => {
  const V = { ghost: { background: 'rgba(255,255,255,.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,.08)' }, amber: { background: 'rgba(245,158,11,.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,.3)' }, violet: { background: 'rgba(139,92,246,.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,.3)' }, green: { background: 'rgba(16,185,129,.15)', color: '#34d399', border: '1px solid rgba(16,185,129,.3)' }, red: { background: 'rgba(239,68,68,.15)', color: '#f87171', border: '1px solid rgba(239,68,68,.3)' }, yellow: { background: 'rgba(234,179,8,.15)', color: '#facc15', border: '1px solid rgba(234,179,8,.3)' } };
  const v = V[variant] || V.ghost;
  const pad = size === 'xs' ? '3px 8px' : size === 'sm' ? '5px 12px' : '8px 18px';
  return <button onClick={onClick} disabled={disabled} style={{ ...v, padding: pad, borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .45 : 1, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'opacity .15s', whiteSpace: 'nowrap', ...style }}>{children}</button>;
};

const DarkInput = ({ value, onChange, placeholder, style = {} }) => (
  <input value={value} onChange={onChange} placeholder={placeholder} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '8px 12px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none', ...style }} />
);

const DarkSelect = ({ value, onChange, children, style = {} }) => (
  <select value={value} onChange={onChange} style={{ background: '#111827', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '8px 12px', color: '#e2e8f0', fontSize: 12, outline: 'none', appearance: 'none', cursor: 'pointer', ...style }}>{children}</select>
);

const StoreAvatar = ({ store, size = 40 }) => {
  const gs = [['#a78bfa','#7c3aed'],['#f59e0b','#d97706'],['#14b8a6','#0d9488'],['#3b82f6','#2563eb'],['#f43f5e','#e11d48'],['#34d399','#059669']];
  const g = gs[(store.name?.charCodeAt(0) || 0) % gs.length];
  return <div style={{ width: size, height: size, borderRadius: size * .28, background: `linear-gradient(135deg,${g[0]},${g[1]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '2px solid rgba(255,255,255,.1)', boxShadow: `0 0 14px ${g[0]}33` }}><Store size={size * .42} color="#fff" strokeWidth={1.8} /></div>;
};

const ActiveBadge = ({ active, onClick }) => (
  <button onClick={onClick} style={{ background: active ? 'rgba(16,185,129,.12)' : 'rgba(100,116,139,.12)', color: active ? '#34d399' : '#94a3b8', boxShadow: `inset 0 0 0 1px ${active ? 'rgba(16,185,129,.3)' : 'rgba(100,116,139,.25)'}`, fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 20, cursor: onClick ? 'pointer' : 'default', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, transition: 'all .15s' }}>
    {active ? <><ToggleRight size={11} />Active</> : <><ToggleLeft size={11} />Inactive</>}
  </button>
);

const VerifiedBadge = ({ verified, onVerify }) => verified
  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(139,92,246,.12)', color: '#a78bfa', boxShadow: 'inset 0 0 0 1px rgba(139,92,246,.3)', fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 20 }}><ShieldCheck size={10} />Verified</span>
  : <button onClick={onVerify} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(234,179,8,.12)', color: '#facc15', boxShadow: 'inset 0 0 0 1px rgba(234,179,8,.3)', fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 20, cursor: 'pointer', border: 'none', transition: 'all .15s' }}><Eye size={10} />Verify</button>;

const PaymentBadge = ({ status }) => {
  const m = { completed: ['rgba(16,185,129,.12)','#34d399','rgba(16,185,129,.3)'], pending: ['rgba(245,158,11,.12)','#fbbf24','rgba(245,158,11,.3)'], failed: ['rgba(239,68,68,.12)','#f87171','rgba(239,68,68,.3)'] };
  const [bg, color, ring] = m[status] || m.pending;
  return <span style={{ background: bg, color, boxShadow: `inset 0 0 0 1px ${ring}`, fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 20 }}>{status || 'pending'}</span>;
};

const ConfirmDialog = ({ open, message, onConfirm, onCancel }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 60 }}>
        <motion.div initial={{ scale: .92, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .92 }} style={{ background: '#0d1526', border: '1px solid rgba(255,255,255,.12)', borderRadius: 16, padding: 28, maxWidth: 360, width: '100%', textAlign: 'center' }}>
          <AlertTriangle size={28} color="#f87171" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 }}>Confirm Delete</p>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 22, lineHeight: 1.6 }}>{message}</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
            <Btn variant="red" onClick={onConfirm}><Trash2 size={12} /> Delete</Btn>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ═══════════════════════════════════════════════════════════════════════════
export default function PiStores() {
  const [stores, setStores]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedStore, setSelectedStore] = useState(null);
  const [showModal, setShowModal]       = useState(false);
  const [modalTab, setModalTab]         = useState('info');
  const [searchTerm, setSearchTerm]     = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [filtersOpen, setFiltersOpen]   = useState(false);
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const formatDate = ts => {
    if (!ts) return 'N/A';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const loadStores = useCallback(async () => {
    setLoading(true);
    try { setStores(await getAllStores()); }
    catch { toast.error('Failed to load stores'); }
    finally { setLoading(false); }
  }, []);

  // ─── ÉCOUTE EN TEMPS RÉEL ──────────────────────────────────────────────
useEffect(() => {
  setLoading(true);
  
  const unsubscribe = listenAllStores(
    (stores) => {
      setStores(stores);
      setLoading(false);
    },
    (error) => {
      console.error('❌ Stores listener error:', error);
      toast.error('Failed to load stores');
      setLoading(false);
    }
  );
  
  return () => {
    if (unsubscribe) unsubscribe();
  };
}, []);
  // ═══════════════════════════════════════════════════════════════════════════
// WHATSAPP
// ═══════════════════════════════════════════════════════════════════════════
// ℹ️ Le WhatsApp 'stor_activated' et 'store_verified' est envoyé uniquement par
// le trigger Cloud Function `onStoreActivated` (index.js), pas depuis ce fichier.
// Voir handleToggleStatus / handleVerify ci-dessous.

// sapi-admin/src/pages/PiStores.jsx

// ─── Gestion du basculement ACTIF/INACTIF ────────────────────────────────
const handleToggleStatus = async (storeId, current) => {
  try {
    const storeToUpdate = stores.find(s => s.id === storeId);
    if (!storeToUpdate) {
      toast.error('Store not found');
      return;
    }

    // ✅ VÉRIFICATION : Si on essaie d'activer un store non vérifié
    if (!current && !storeToUpdate.isVerified) {
      toast.error('❌ Cannot activate a store that is not verified. Please verify the store first.');
      return;
    }

    const newStatus = !current;
    
    // Mettre à jour Firestore
    await toggleStoreStatus(storeId, newStatus);
    
    // Mettre à jour l'état local
    setStores(p => p.map(s => s.id === storeId ? { ...s, isActive: newStatus } : s));
    if (selectedStore?.id === storeId) {
      setSelectedStore(p => ({ ...p, isActive: newStatus }));
    }
    
    // ✅ Si on active la boutique (INACTIVE → ACTIVE)
    if (newStatus && storeToUpdate) {
      // Notification push au propriétaire
      await notifyStoreOwner(storeToUpdate.id, storeToUpdate.name, storeToUpdate.userId);
      
      // ℹ️ WhatsApp 'stor_activated' n'est PAS envoyé ici. Le trigger Cloud Function
      // `onStoreActivated` (index.js) écoute ce même write Firestore (isActive:
      // false → true) et envoie déjà le template. L'envoyer aussi ici causait un
      // double message WhatsApp (voir bug du 01/08/2026).
      
      toast.success(`✅ Store "${storeToUpdate.name}" activated! Notifications sent.`);
    } else {
      toast.success(`Store ${newStatus ? 'activated' : 'deactivated'}`);
    }
  } catch (error) {
    console.error('❌ Toggle error:', error);
    toast.error('Failed to update store status: ' + error.message);
  }
};

 // sapi-admin/src/pages/PiStores.jsx - Dans handleVerify

  const handleVerify = async (storeId) => {
    try {
      const storeToVerify = stores.find(s => s.id === storeId);
      
      await verifyStore(storeId);
      setStores(p => p.map(s => s.id === storeId ? { ...s, isVerified: true } : s));
      if (selectedStore?.id === storeId) {
        setSelectedStore(p => ({ ...p, isVerified: true }));
      }
      
      // ✅ Notification push au propriétaire
      if (storeToVerify && storeToVerify.userId) {
        await notifyStoreOwner(storeId, storeToVerify.name, storeToVerify.userId);
      }
      
      // ✅ Notification admin
      toast.success(`✅ Store "${storeToVerify?.name}" verified! It can now be activated.`);
      
    } catch (error) {
      console.error('❌ Verification error:', error);
      toast.error('Failed to verify store: ' + error.message);
    }
  };

  const handleDeleteRequest = id => { setPendingDeleteId(id); setConfirmOpen(true); };

  // Confirmation dialog for deletion
  const handleDeleteConfirm = async () => {
    console.log('🗑️ Deleting store:', pendingDeleteId);
    try {
      const result = await deleteStore(pendingDeleteId);
      console.log('✅ Delete result:', result);
      
      // Mettre à jour l'état local
      setStores(prevStores => prevStores.filter(s => s.id !== pendingDeleteId));
      
      // Si la boutique supprimée était dans le modal, fermer le modal
      if (selectedStore?.id === pendingDeleteId) {
        setShowModal(false);
        setSelectedStore(null);
      }
      
      toast.success('Store deleted successfully');
    } catch (error) {
      console.error('❌ Delete failed:', error);
      toast.error('Failed to delete store: ' + error.message);
    } finally {
      setConfirmOpen(false);
      setPendingDeleteId(null);
    }
  };

  // Envoyer une notification push au propriétaire de la boutique
const notifyStoreOwner = async (storeId, storeName, ownerUserId) => {
  if (!ownerUserId) {
    console.log('⚠️ No ownerUserId for store', storeId);
    return;
  }
  
  try {
    const response = await fetch('https://sendnotificationtouser-b3shwrnirq-uc.a.run.app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: ownerUserId,
        title: '🏪 Votre boutique est active !',
        body: `Félicitations ! Votre boutique "${storeName}" est maintenant visible sur Sapi.`,
        data: {
          type: 'store_activated',
          storeId: storeId,
          storeName: storeName,
        },
      }),
    });
    
    if (response.ok) {
      console.log(`✅ Notification envoyée au propriétaire de ${storeName}`);
    } else {
      console.log('⚠️ Échec envoi notification');
    }
  } catch (error) {
    console.error('❌ Erreur envoi notification:', error);
  }
};

  const filteredStores = useMemo(() => {
    let f = [...stores];
    if (searchTerm.trim()) { const s = searchTerm.toLowerCase(); f = f.filter(st => st.name?.toLowerCase().includes(s) || st.description?.toLowerCase().includes(s) || st.address?.city?.toLowerCase().includes(s) || st.address?.country?.toLowerCase().includes(s) || st.contact?.email?.toLowerCase().includes(s)); }
    if (filterStatus === 'active')     f = f.filter(s => s.isActive);
    if (filterStatus === 'inactive')   f = f.filter(s => !s.isActive);
    if (filterVerified === 'verified') f = f.filter(s => s.isVerified);
    if (filterVerified === 'unverified') f = f.filter(s => !s.isVerified);
    return f;
  }, [stores, searchTerm, filterStatus, filterVerified]);

  const stats = useMemo(() => ({
    total: stores.length,
    active: stores.filter(s => s.isActive).length,
    pending: stores.filter(s => !s.isVerified && s.isActive).length,
    revenue: (stores.filter(s => s.paymentStatus === 'completed').length * 0.5).toFixed(1),
  }), [stores]);

  const hasActiveFilters = filterStatus !== 'all' || filterVerified !== 'all' || searchTerm;
  const clearFilters = () => { setSearchTerm(''); setFilterStatus('all'); setFilterVerified('all'); };
  const COL = '52px 1fr 140px 130px 100px 110px 90px 110px';

  if (loading && stores.length === 0) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(6,182,212,.3)', borderTopColor: '#06b6d4', animation: 'spin .8s linear infinite' }} />  
      <p style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Loading Pi Stores…</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ paddingBottom: 48 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input::placeholder{color:#334155}`}</style>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.18em', color: 'cyan', marginBottom: 4 }}>Management</p>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
              <Store size={20} color="cyan" strokeWidth={2} /> Pi Stores
            </h1>
            <p style={{ fontSize: 11, color: '#475569' }}>Manage and verify registered Pi Network stores</p>
          </div>
          <Btn variant="ghost" onClick={loadStores} disabled={loading}>
            <RefreshCw size={13} style={{ animation: loading ? 'spin .8s linear infinite' : 'none' }} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </Btn>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
          <MiniStat label="Total Stores"         value={stats.total}         accent="#a78bfa" icon={Store}       index={0} />
          <MiniStat label="Active"               value={stats.active}        accent="#34d399" icon={CheckCircle} index={1} />
          <MiniStat label="Pending Verification" value={stats.pending}       accent="#facc15" icon={Eye}         index={2} />
          <MiniStat label="Revenue (π)"          value={`${FormatPrice(stats.revenue)}`} accent="#f59e0b" icon={Star}       index={3} />
        </div>
      </div>

      {/* Filters */}
      <GlassPanel style={{ marginBottom: 14 }}>
        <button onClick={() => setFiltersOpen(v => !v)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', color: '#e2e8f0' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600 }}>
            <Filter size={13} color="#a78bfa" /> Filters &amp; Search
            {hasActiveFilters && <span style={{ background: 'rgba(167,139,250,.2)', color: '#a78bfa', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 20, border: '1px solid rgba(167,139,250,.3)' }}>ACTIVE</span>}
          </span>
          <ChevronDown size={13} style={{ transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s', color: '#475569' }} />
        </button>
        <AnimatePresence>
          {filtersOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: .2 }} style={{ overflow: 'hidden' }}>
              <div style={{ padding: '0 16px 16px', borderTop: '1px solid rgba(255,255,255,.05)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginTop: 14 }}>
                  <div>
                    <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 6 }}>Search</label>
                    <div style={{ position: 'relative' }}>
                      <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }} />
                      <DarkInput value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Name, city, email…" style={{ paddingLeft: 28 }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 6 }}>Status</label>
                    <DarkSelect value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                      <option value="all">All</option><option value="active">Active</option><option value="inactive">Inactive</option>
                    </DarkSelect>
                  </div>
                  <div>
                    <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 6 }}>Verification</label>
                    <DarkSelect value={filterVerified} onChange={e => setFilterVerified(e.target.value)}>
                      <option value="all">All</option><option value="verified">Verified</option><option value="unverified">Unverified</option>
                    </DarkSelect>
                  </div>
                </div>
                {hasActiveFilters && <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}><Btn variant="red" size="xs" onClick={clearFilters}><X size={11} /> Clear</Btn></div>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </GlassPanel>

      {/* ─── Table ── */}
      <GlassPanel>
        <div style={{ display: 'grid', gridTemplateColumns: COL, gap: 8, padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,.05)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#334155', fontWeight: 700, alignItems: 'center' }}>
          <div /><div>Store</div><div>Location</div><div>Contact</div><div>Status</div><div>Verified</div><div>Registered</div><div>Actions</div>
        </div>

        {filteredStores.length === 0 && !loading && (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <Store size={32} style={{ margin: '0 auto 12px', opacity: .2, display: 'block', color: '#475569' }} />
            <p style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>No stores found</p>
          </div>
        )}

        {filteredStores.map((store, idx) => (
          <motion.div key={store.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(idx * .025, .4) }}
            style={{ display: 'grid', gridTemplateColumns: COL, gap: 8, padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,.03)', alignItems: 'center', transition: 'background .15s', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={() => { setSelectedStore(store); setModalTab('info'); setShowModal(true); }}
          >
            {/* Avatar */}
            <div onClick={e => e.stopPropagation()}><StoreAvatar store={store} size={38} /></div>

            {/* Store Info */}
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', marginBottom: 2 }}>{store.name}</p>
              <p style={{ fontSize: 10, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{store.description}</p>
              <p style={{ fontSize: 10, color: '#a78bfa', marginTop: 2 }}>{store.products?.length || 0} products</p>
            </div>

            {/* Location */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5 }}>
              <MapPin size={11} color="#475569" style={{ marginTop: 1, flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{store.address?.city || '—'}</p>
                <p style={{ fontSize: 10, color: '#475569' }}>{store.address?.country || '—'}</p>
              </div>
            </div>

            {/* Contact */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>
                <Phone size={10} color="#475569" />{store.contact?.phone || '—'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: '#475569', overflow: 'hidden' }}>
                <Mail size={10} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{store.contact?.email || '—'}</span>
              </div>
            </div>

            {/* ✅ Status - Activation conditionnelle */}
            <div onClick={e => e.stopPropagation()}>
              {store.isVerified ? (
                // ✅ Bouton Activate/Deactivate (si vérifié)
                <ActiveBadge active={store.isActive} onClick={() => handleToggleStatus(store.id, store.isActive)} />
              ) : (
                // ❌ Message "Verify First" (si non vérifié)
                <span style={{ 
                  background: 'rgba(100,116,139,.12)', 
                  color: '#94a3b8', 
                  boxShadow: 'inset 0 0 0 1px rgba(100,116,139,.25)',
                  fontSize: 9, 
                  fontWeight: 700, 
                  letterSpacing: '.06em', 
                  textTransform: 'uppercase', 
                  padding: '3px 8px', 
                  borderRadius: 20,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'not-allowed'
                }}>
                  <span style={{ fontSize: 11 }}>🔒</span> Verify First
                </span>
              )}
            </div>

            {/* ✅ Verified Badge avec onVerify */}
            <div onClick={e => e.stopPropagation()}>
              <VerifiedBadge verified={store.isVerified} onVerify={() => handleVerify(store.id)} />
            </div>

            {/* Registered Date */}
            <div style={{ fontSize: 10, color: '#475569' }}>{formatDate(store.createdAt)}</div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
              <Btn size="xs" variant="ghost" onClick={() => { setSelectedStore(store); setModalTab('info'); setShowModal(true); }}>
                <Eye size={10} />
              </Btn>
              <Btn size="xs" variant="red" onClick={() => handleDeleteRequest(store.id)}>
                <Trash2 size={10} />
              </Btn>
            </div>
          </motion.div>
        ))}

        {filteredStores.length > 0 && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,.04)' }}>
            <p style={{ fontSize: 10, color: '#334155' }}>
              Showing <span style={{ color: '#a78bfa', fontWeight: 700 }}>{filteredStores.length}</span> of <span style={{ color: '#a78bfa', fontWeight: 700 }}>{stores.length}</span> stores
            </p>
          </div>
        )}
      </GlassPanel>

      {/* Modal */}
      <AnimatePresence>
        {showModal && selectedStore && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.68)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 }} onClick={() => setShowModal(false)}>
            <motion.div initial={{ scale: .95, opacity: 0, y: 16 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: .95, opacity: 0, y: 16 }} transition={{ duration: .22, ease: [.22,1,.36,1] }}
              style={{ background: '#0d1526', border: '1px solid rgba(255,255,255,.1)', borderRadius: 18, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal header */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', gap: 14 }}>
                <StoreAvatar store={selectedStore} size={46} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedStore.name}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
                    <ActiveBadge active={selectedStore.isActive} />
                    <VerifiedBadge verified={selectedStore.isVerified} onVerify={() => handleVerify(selectedStore.id)} />
                    <PaymentBadge status={selectedStore.paymentStatus} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {['info', 'products'].map(tab => (
                    <button key={tab} onClick={() => setModalTab(tab)} style={{ padding: '4px 12px', borderRadius: 8, fontSize: 10, fontWeight: 600, cursor: 'pointer', background: modalTab === tab ? 'rgba(167,139,250,.15)' : 'rgba(255,255,255,.05)', color: modalTab === tab ? '#a78bfa' : '#475569', border: modalTab === tab ? '1px solid rgba(167,139,250,.3)' : '1px solid rgba(255,255,255,.08)', transition: 'all .15s' }}>
                      {tab === 'info' ? 'Info' : `Products (${selectedStore.products?.length || 0})`}
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowModal(false)} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', color: '#94a3b8', display: 'flex', flexShrink: 0 }}><X size={14} /></button>
              </div>

              {/* Modal body */}
              <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
                {modalTab === 'info' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* KPIs */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                      {[
                        { label: 'Products', value: selectedStore.products?.length || 0, accent: '#a78bfa' },
                        { label: 'Payment',  value: selectedStore.paymentStatus || 'pending', accent: selectedStore.paymentStatus === 'completed' ? '#34d399' : '#facc15' },
                        { label: 'Since',    value: formatDate(selectedStore.createdAt), accent: '#38bdf8' },
                      ].map(({ label, value, accent }) => (
                        <div key={label} style={{ background: `${accent}0d`, border: `1px solid ${accent}25`, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                          <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: accent, opacity: .75, marginBottom: 5 }}>{label}</p>
                          <p style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Description */}
                    <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.05)', borderRadius: 10, padding: '12px 14px' }}>
                      <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#334155', marginBottom: 6 }}>Description</p>
                      <p style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>{selectedStore.description || '—'}</p>
                    </div>

                    {/* Location + Contact */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.05)', borderRadius: 10, padding: '12px 14px' }}>
                        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#334155', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}><MapPin size={10} color="#a78bfa" />Location</p>
                        <p style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.7 }}>
                          {selectedStore.address?.street || '—'}<br />
                          {selectedStore.address?.city}{selectedStore.address?.postalCode ? `, ${selectedStore.address.postalCode}` : ''}<br />
                          <span style={{ fontWeight: 600, color: '#cbd5e1' }}>{selectedStore.address?.country || '—'}</span>
                        </p>
                        {selectedStore.coordinates?.latitude && <p style={{ fontSize: 10, color: '#334155', marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}><Navigation size={9} />{selectedStore.coordinates.latitude.toFixed(4)}, {selectedStore.coordinates.longitude.toFixed(4)}</p>}
                      </div>
                      <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.05)', borderRadius: 10, padding: '12px 14px' }}>
                        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#334155', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}><Phone size={10} color="#a78bfa" />Contact</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#94a3b8', marginBottom: 7 }}><Phone size={11} color="#475569" />{selectedStore.contact?.phone || '—'}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, color: '#94a3b8' }}><Mail size={11} color="#475569" /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedStore.contact?.email || '—'}</span></div>
                      </div>
                    </div>

                    {/* Actions */}

                    <div style={{ display: 'flex', gap: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,.05)', flexWrap: 'wrap' }}>
                      {/* ✅ Bouton Activer/Deactivate avec condition */}
                      {selectedStore.isVerified ? (
                        <Btn 
                          variant={selectedStore.isActive ? 'ghost' : 'green'} 
                          style={{ flex: 1, justifyContent: 'center' }} 
                          onClick={() => handleToggleStatus(selectedStore.id, selectedStore.isActive)}
                        >
                          {selectedStore.isActive ? <><ToggleLeft size={12} />Deactivate</> : <><ToggleRight size={12} />Activate</>}
                        </Btn>
                      ) : (
                        <Btn 
                          variant="ghost" 
                          style={{ flex: 1, justifyContent: 'center', opacity: 0.5, cursor: 'not-allowed' }} 
                          disabled={true}
                          title="Store must be verified first"
                        >
                          <ToggleLeft size={12} /> Activate (Verify first)
                        </Btn>
                      )}
                      
                      {!selectedStore.isVerified && (
                        <Btn variant="violet" style={{ flex: 1, justifyContent: 'center' }} onClick={() => handleVerify(selectedStore.id)}>
                          <ShieldCheck size={12} /> Verify Store
                        </Btn>
                      )}
                      
                      <Btn variant="red" onClick={() => { setShowModal(false); handleDeleteRequest(selectedStore.id); }}>
                        <Trash2 size={12} /> Delete
                      </Btn>
                    </div>
                  </div>
                )}

                {modalTab === 'products' && (
                  <div>
                    {(!selectedStore.products?.length)
                      ? <div style={{ padding: '32px 0', textAlign: 'center' }}><Package size={28} style={{ margin: '0 auto 10px', opacity: .25, display: 'block', color: '#475569' }} /><p style={{ color: '#334155', fontSize: 12 }}>No products listed</p></div>
                      : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {selectedStore.products.map((product, idx) => (
                            <motion.div key={idx} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * .06 }}
                              style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(139,92,246,.15)', borderRadius: 12, padding: '14px 16px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: product.description ? 8 : 0 }}>
                                <p style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{product.type || product.name || `Product ${idx + 1}`}</p>
                                <span style={{ background: 'rgba(167,139,250,.15)', color: '#a78bfa', border: '1px solid rgba(167,139,250,.3)', fontSize: 12, fontWeight: 800, padding: '2px 10px', borderRadius: 20 }}>{product.priceInPi} π</span>
                              </div>
                              {product.description && <p style={{ fontSize: 11, color: '#64748b', lineHeight: 1.55 }}>{product.description}</p>}
                            </motion.div>
                          ))}
                        </div>
                    }
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog open={confirmOpen} message="This action is permanent. The store and all its data will be removed." onConfirm={handleDeleteConfirm} onCancel={() => { setConfirmOpen(false); setPendingDeleteId(null); }} />
    </motion.div>
  );
}